import { NgClass, CommonModule } from '@angular/common';
import {
  Component,
  signal,
  ViewEncapsulation,
  ViewChild,
  input,
  forwardRef,
  computed,
} from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import {
  MAT_DATE_LOCALE,
  provideNativeDateAdapter,
} from '@angular/material/core';
import {
  MatDatepickerModule,
  MatDatepicker,
  MatDatepickerIntl,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SpanishMatDatepickerIntl } from '../../../../shared/utils/spanish-mat-datepicker-intl';

@Component({
  selector: 'app-selector-datepicker',
  imports: [
    CommonModule,
    MatChipsModule,
    NgClass,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
    { provide: MatDatepickerIntl, useClass: SpanishMatDatepickerIntl },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectorDatepickerComponent),
      multi: true,
    },
  ],
  templateUrl: './selector-datepicker.component.html',
  styleUrl: './selector-datepicker.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class SelectorDatepickerComponent implements ControlValueAccessor {
  @ViewChild('picker') picker!: MatDatepicker<Date>;
  disabled = input<boolean>(false);
  maxDate = new Date(); // Fecha máxima para el datepicker, por defecto hoy
  // Opcional: mínima permitida
  minDate: Date | null = new Date('2024-01-01');

  selectableDates = input<Date[]>([]);

  selectedDate = signal<Date | null>(null);
  isCalendarOpen = signal<boolean>(false);
  private formDisabled = signal<boolean>(false);
  isDisabled = computed(() => !!this.disabled() || this.formDisabled());

  // ControlValueAccessor implementation
  private onChange = (value: Date | null) => {};
  private onTouched = () => {};
  private _disabled = false;

  get sortedSelectableDates(): Date[] {
    return this.selectableDates()
      .map(d => new Date(d))
      .sort((a, b) => a.getTime() - b.getTime());
  }

  private atNoon(d: Date): Date {
    const x = new Date(d);
    x.setHours(12, 0, 0, 0);
    return x;
  }

  private isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  openDatepicker() {
    this.picker.open();
    this.isCalendarOpen.set(true);
  }

  onDatepickerClosed() {
    this.isCalendarOpen.set(false);
  }

  toggleCalendar() {
    this.isCalendarOpen.set(!this.isCalendarOpen());
  }

  closeCalendar(event: Event) {
    this.isCalendarOpen.set(false);
  }

  onDateChange(date: Date | null) {
    if (date) {
      const normalized = this.atNoon(date);
      this.selectedDate.set(normalized);
      this.onChange(normalized);
      this.onTouched();
    }
  }

  // Funcion que salta el datepicker al dia anterior
  prevDay() {
    if (this.isDisabled()) return;
    const dates = this.sortedSelectableDates;
    const hasList = dates.length > 0;
    let next: Date | null = null;

    if (hasList) {
      const current = this.selectedDate();
      if (!current) {
        const today = this.atNoon(new Date());
        const idx = dates.findIndex(d => d > today);
        if (idx > 0) next = dates[idx - 1];
        else if (dates.length > 0) next = dates[dates.length - 1];
      } else {
        const currentIdx = dates.findIndex(d => this.isSameDay(d, current!));
        next = currentIdx > 0 ? dates[currentIdx - 1] : dates[dates.length - 1];
      }
    } else {
      const base = this.selectedDate() ?? this.atNoon(new Date());
      next = new Date(base);
      next.setDate(base.getDate() - 1);
      next = this.atNoon(next);
      if (this.minDate && next < this.atNoon(this.minDate)) {
        next = this.atNoon(this.minDate);
      }
    }

    if (next) {
      const normalized = this.atNoon(next);
      this.selectedDate.set(normalized);
      this.onChange(normalized);
      this.onTouched();
    }
  }

  nextDay() {
    if (this.isDisabled()) return;
    const dates = this.sortedSelectableDates;
    const hasList = dates.length > 0;
    let next: Date | null = null;

    if (hasList) {
      const current = this.selectedDate();
      if (!current) {
        const today = this.atNoon(new Date());
        const idx = dates.findIndex(d => d > today);
        if (idx >= 0) next = dates[idx];
        else if (dates.length > 0) next = dates[0];
      } else {
        const currentIdx = dates.findIndex(d => this.isSameDay(d, current!));
        next = currentIdx < dates.length - 1 ? dates[currentIdx + 1] : dates[0];
      }
    } else {
      const base = this.selectedDate() ?? this.atNoon(new Date());
      next = new Date(base);
      next.setDate(base.getDate() + 1);
      next = this.atNoon(next);
      if (this.maxDate && next > this.atNoon(this.maxDate)) {
        next = this.atNoon(this.maxDate);
      }
    }

    if (next) {
      const normalized = this.atNoon(next);
      this.selectedDate.set(normalized);
      this.onChange(normalized);
      this.onTouched();
    }
  }

  get isPrevDisabled(): boolean {
    if (this.isDisabled()) return true;
    const dates = this.sortedSelectableDates;
    const hasList = dates.length > 0;
    if (hasList) {
      if (dates.length <= 1) return true;
      const current = this.selectedDate();
      if (!current) return false;
      const currentIdx = dates.findIndex(d => this.isSameDay(d, current));
      return currentIdx <= 0;
    }
    if (!this.minDate) return false;
    const curr = this.atNoon(this.selectedDate() ?? new Date());
    return curr <= this.atNoon(this.minDate);
  }

  get isNextDisabled(): boolean {
    if (this.isDisabled()) return true;
    const dates = this.sortedSelectableDates;
    const hasList = dates.length > 0;
    if (hasList) {
      if (dates.length <= 1) return true;
      const current = this.selectedDate();
      if (!current) return false;
      const currentIdx = dates.findIndex(d => this.isSameDay(d, current));
      return currentIdx >= dates.length - 1;
    }
    if (!this.maxDate) return false;
    const curr = this.atNoon(this.selectedDate() ?? new Date());
    return curr >= this.atNoon(this.maxDate);
  }

  myFilter = (d: Date | null): boolean => {
    if (!d) return false;
    const list = this.selectableDates();
    if (!list || list.length === 0) return true;
    return list.some(date => this.isSameDay(date, d));
  };

  // ControlValueAccessor implementation
  writeValue(value: Date | null): void {
    this.selectedDate.set(value ? this.atNoon(value) : null);
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
    this.formDisabled.set(!!isDisabled);
  }
}
